import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useToast } from "../../services/utils";

interface QueryTemplatesCardProps {
  templates: string[];
  title?: string;
  onQuerySelect?: (query: string) => void;
  showCopyOnly?: boolean;
}

const QueryTemplatesCard: React.FC<QueryTemplatesCardProps> = ({
  templates,
  title = "Query Templates",
  onQuerySelect,
  showCopyOnly = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { showToast } = useToast();

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCopyQuery = async (queryText: string) => {
    try {
      await navigator.clipboard.writeText(queryText);
      showToast("Query copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy query:", err);
      showToast("Failed to copy query", "error");
    }
  };

  const handleQuerySelect = (queryText: string) => {
    if (onQuerySelect) {
      onQuerySelect(queryText);
    }
  };

  return (
    <Grid item xs={12}>
      <Card
        className={`query-template-collapsible ${
          isExpanded ? "opened" : "closed"
        }`}
      >
        <CardHeader
          onClick={handleExpandClick}
          title={
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              {title}
            </Typography>
          }
          action={
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              aria-expanded={isExpanded}
              aria-label="show more"
              sx={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          }
        />
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            {/* <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
              Available Query Templates:
            </Typography> */}
            <Box>
              {templates.map((queryText: string, index: number) => (
                <Box className="template-item" key={index}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      flex: 1,
                      mr: 2,
                      fontFamily: "monospace",
                    }}
                  >
                    {queryText}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {showCopyOnly ? (
                      <Tooltip title="Copy query to clipboard" placement="top">
                        <IconButton
                          onClick={() => handleCopyQuery(queryText)}
                          size="small"
                          sx={{
                            color: "#537ea0",
                            "&:hover": {
                              backgroundColor: "rgba(83, 126, 160, 0.1)",
                            },
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip
                          title="Copy query to clipboard"
                          placement="top"
                        >
                          <IconButton
                            onClick={() => handleCopyQuery(queryText)}
                            size="small"
                            sx={{
                              color: "#537ea0",
                              "&:hover": {
                                backgroundColor: "rgba(83, 126, 160, 0.1)",
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleQuerySelect(queryText)}
                          sx={{
                            color: "#537ea0",
                            borderColor: "#537ea0",
                            "&:hover": {
                              borderColor: "#537ea0",
                              backgroundColor: "rgba(83, 126, 160, 0.04)",
                            },
                          }}
                        >
                          Use
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    </Grid>
  );
};

export default QueryTemplatesCard;
